import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Build, CloudDownload, EditNote, Refresh, Storage, Update } from "@mui/icons-material";
import { EditNotesDialog } from "../components/archive/EditNotesDialog";
import { getInventory, InventoryRow, refreshInventory } from "../api/inventory";
import { listJobs, NON_TERMINAL_JOB_STATUSES } from "../api/jobs";
import {
  submitBatchOnboard,
  submitBatchRebuild,
  submitYearOnboard,
  SubmitWorkflowResponse,
} from "../api/workflows";
import { formatBytes, formatTs } from "../api/workflowSummary";
import { getMe, MeResponse } from "../api/me";
import { ApiError, extractErrorMessage } from "../api/client";

// Admin: archive inventory + per-month indexing actions.
//
// Data comes from /api/v1/operator/archive/inventory which the wizard
// also uses for month-eligibility gating. Two layers of action live
// on this page:
//
// Page-level (admin):
//   - "Refresh inventory" → submits a SCAN job that walks the archive
//                  bucket and re-populates the inventory table. The
//                  page then polls /inventory for ~30s so new rows
//                  surface as the scan completes. Read-only on the
//                  index itself; just refreshes the listing.
//
// Per-row (admin):
//   - "Onboard"  → first-build for a missing month. Submits
//                  batch_onboard (skip_existing=true is the default).
//   - "Update"   → daily-driver for an already-indexed month. Picks
//                  up newly-arrived zips without re-indexing what's
//                  already there, then re-rolls. Same kind as Onboard
//                  underneath — the label is a UX cue, not a different
//                  backend op.
//   - "Rebuild"  → full re-index from scratch (skip_existing=false).
//                  Used after a schema bump or to recover a corrupted
//                  run. Heavier than Update; not the daily op.
//
// Per-row submissions land as workflows just like exports do; the page
// navs to the workflow detail page so the admin can watch progress
// + cancel.

interface RowKey {
  content_type: string;
  year: string;
  month: string;
}

// Includes content_type: the same (year, month) exists once per content
// type (broadcast, podcast, …), so keying on year-month alone collides.
const keyOf = (r: RowKey) => `${r.content_type}-${r.year}-${r.month}`;

// "omcs-podcast" → "podcast" for display; everything else unchanged.
const prettyContentType = (ct: string) => ct.replace(/^omcs-/, "");

export function AdminArchive() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<InventoryRow[] | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submittingKey, setSubmittingKey] = useState<string | null>(null);
  // True while the operator has at least one SCAN job in a non-terminal
  // status (queued/starting/running). Driven by the polling effect
  // below — works for scans we just kicked AND for scans someone else
  // started elsewhere. `null` = haven't checked yet (initial state).
  const [scanInProgress, setScanInProgress] = useState<boolean | null>(null);
  // Month whose errata dialog is open (null = closed).
  const [notesTarget, setNotesTarget] = useState<RowKey | null>(null);

  const loadInventory = () =>
    getInventory()
      .then((r) => setRows(r.inventory))
      .catch((err) => setError(`inventory load failed: ${err}`));

  useEffect(() => {
    loadInventory();
    getMe()
      .then(setMe)
      .catch(() => undefined);
  }, []);

  // Poll for in-flight scans every 5s. When a scan goes from
  // in-progress → done, refetch inventory so the freshly-discovered
  // rows surface. The polling runs the entire time the page is
  // mounted; cheap (one small request) and means the SPA stays in sync
  // even when an admin elsewhere kicks a scan.
  useEffect(() => {
    let cancelled = false;
    let lastInProgress: boolean | null = null;

    const tick = async () => {
      try {
        const r = await listJobs({ kind: "scan", limit: 5 });
        if (cancelled) return;
        const inFlight = r.jobs.some((j) =>
          NON_TERMINAL_JOB_STATUSES.has(j.status),
        );
        // Edge: scan just finished. Refetch inventory so the new rows
        // show up. Don't refetch on every tick — only on the transition.
        if (lastInProgress === true && inFlight === false) {
          loadInventory();
        }
        lastInProgress = inFlight;
        setScanInProgress(inFlight);
      } catch {
        // Quietly skip — next tick retries. The page is still useful
        // even if scan-status checking is temporarily failing.
      } finally {
        if (!cancelled) setTimeout(tick, 5000);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRefresh = async () => {
    setError(null);
    try {
      await refreshInventory();
      // Optimistic: don't wait for the next poll tick to flip the
      // button — show progress immediately. The poll will confirm or
      // (rarely) clear it.
      setScanInProgress(true);
    } catch (err) {
      setError(`refresh failed: ${err}`);
    }
  };

  const archiveBucket = me?.default_archive_bucket ?? null;

  const byYear = useMemo(() => {
    if (!rows) return [] as Array<{ year: string; rows: InventoryRow[] }>;
    const map = new Map<string, InventoryRow[]>();
    for (const r of rows) {
      const list = map.get(r.year) ?? [];
      list.push(r);
      map.set(r.year, list);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[0].localeCompare(a[0])) // newest year first
      .map(([year, ys]) => ({
        year,
        // newest month first; within a month, group by content_type so the
        // per-content-type rows for the same month sit adjacent.
        rows: ys.sort(
          (a, b) =>
            b.month.localeCompare(a.month) || a.content_type.localeCompare(b.content_type),
        ),
      }));
  }, [rows]);

  // Submit a single year_onboard for every month of $year that isn't
  // already indexed. The operator's year_onboard shim builds 12
  // month-jobs with skip_existing=true and the post-0.28.5 default
  // concurrency=6, so this is "scan + bring everything new in this
  // year up to date" in one click — matches the daily-driver
  // mental model better than 12 separate per-month buttons.
  const submitYear = async (year: string) => {
    if (!archiveBucket) {
      setError(
        "no archive bucket configured — set PAIM_DEFAULT_ARCHIVE_BUCKET on the portal",
      );
      return;
    }
    const rowKey = `year-${year}`;
    setSubmittingKey(rowKey);
    setError(null);
    try {
      const r = await submitYearOnboard({ year, bucket: archiveBucket });
      navigate(`/exports/${r.workflow_id}`);
    } catch (err) {
      setError(
        extractErrorMessage(
          err,
          err instanceof ApiError ? `submit returned ${err.status}` : undefined,
        ),
      );
      setSubmittingKey(null);
    }
  };

  const submit = async (
    action: "onboard" | "update" | "rebuild",
    months: string[],
    rowKey: string,
    contentType: string,
  ) => {
    if (!archiveBucket) {
      setError(
        "no archive bucket configured — set PAIM_DEFAULT_ARCHIVE_BUCKET on the portal",
      );
      return;
    }
    setSubmittingKey(rowKey);
    setError(null);
    try {
      // onboard + update map to the same backend kind (batch_onboard
      // with skip_existing=true). The labels diverge in the UI because
      // the user's mental model is different: onboard is "first build",
      // update is "pick up new zips on an already-indexed month".
      const r: SubmitWorkflowResponse =
        action === "rebuild"
          ? await submitBatchRebuild({
              bucket: archiveBucket,
              months,
              content_type: contentType,
            })
          : await submitBatchOnboard({
              bucket: archiveBucket,
              months,
              content_type: contentType,
              skip_existing: true,
            });
      navigate(`/exports/${r.workflow_id}`);
    } catch (err) {
      setError(
        extractErrorMessage(
          err,
          err instanceof ApiError ? `submit returned ${err.status}` : undefined,
        ),
      );
      setSubmittingKey(null);
    }
  };

  if (error && !rows) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  if (!rows) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <CircularProgress size={20} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Stack direction="row" alignItems="center" gap={2}>
          <Storage fontSize="large" color="primary" />
          <Box>
            <Typography variant="h4">Index management</Typography>
            <Typography variant="caption" color="text.secondary">
              Archive bucket:{" "}
              <code>{archiveBucket ?? "(not configured)"}</code>
            </Typography>
          </Box>
        </Stack>
        <Tooltip
          title={
            scanInProgress
              ? "A scan is already running on the operator. The listing will refresh when it finishes."
              : "Scan the archive bucket on S3 and re-populate this listing. Doesn't change the index — just discovers months/zips that exist on disk."
          }
        >
          <span>
            <Button
              variant="outlined"
              startIcon={
                scanInProgress ? <CircularProgress size={16} /> : <Refresh />
              }
              onClick={handleRefresh}
              disabled={scanInProgress === true || scanInProgress === null}
            >
              {scanInProgress ? "Inventory in progress" : "Refresh"}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        {byYear.map(({ year, rows: yearRows }) => (
          <Paper key={year} variant="outlined">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2, py: 1.5 }}
            >
              <Stack direction="row" alignItems="baseline" spacing={2}>
                <Typography variant="h6">{year}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {yearRows.length} months ·{" "}
                  {yearRows.filter((r) => r.has_rollup).length} indexed
                </Typography>
              </Stack>
              <Tooltip
                title={
                  archiveBucket
                    ? `Onboard every month of ${year} that isn't already indexed. Submits a year_onboard workflow (skip_existing=true, default concurrency=6).`
                    : "Configure PAIM_DEFAULT_ARCHIVE_BUCKET on the portal first."
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      submittingKey === `year-${year}` ? (
                        <CircularProgress size={14} />
                      ) : (
                        <Build />
                      )
                    }
                    onClick={() => submitYear(year)}
                    disabled={
                      !archiveBucket ||
                      submittingKey !== null ||
                      // If every month is already indexed, the job is a no-op.
                      yearRows.every((r) => r.has_rollup)
                    }
                  >
                    Onboard year
                  </Button>
                </span>
              </Tooltip>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>content</TableCell>
                  <TableCell>month</TableCell>
                  <TableCell align="right">zips</TableCell>
                  <TableCell>rollup</TableCell>
                  <TableCell>schema</TableCell>
                  <TableCell align="right">rows</TableCell>
                  <TableCell align="right">size</TableCell>
                  <TableCell>last built</TableCell>
                  <TableCell align="right">actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {yearRows.map((r) => {
                  const k = keyOf(r);
                  const monthArg = `${r.year}-${r.month}`;
                  const isSubmitting = submittingKey === k;
                  return (
                    <TableRow key={k} hover>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={r.content_type === "broadcast" ? "primary" : "secondary"}
                          label={prettyContentType(r.content_type)}
                        />
                      </TableCell>
                      <TableCell>{r.month}</TableCell>
                      <TableCell align="right">{r.n_zips.toLocaleString()}</TableCell>
                      <TableCell>
                        {r.has_rollup ? (
                          <Chip size="small" color="success" label="indexed" />
                        ) : (
                          <Chip size="small" color="default" label="missing" />
                        )}
                      </TableCell>
                      <TableCell>
                        {r.rollup_schema_version !== null ? (
                          <code>v{r.rollup_schema_version}</code>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {r.rollup_n_rows !== null ? r.rollup_n_rows.toLocaleString() : "—"}
                      </TableCell>
                      <TableCell align="right">{formatBytes(r.rollup_size_bytes)}</TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatTs(r.rollup_last_built_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                          alignItems="center"
                        >
                          <Tooltip title="Edit per-month errata (capture outages, quality notes)">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setNotesTarget({
                                  content_type: r.content_type,
                                  year: r.year,
                                  month: r.month,
                                })
                              }
                            >
                              <EditNote fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {r.has_rollup ? (
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                          >
                            <Tooltip title="Pick up newly-arrived zips. Skips already-indexed ones, then re-rolls. Submits a workflow.">
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<Update />}
                                  disabled={!archiveBucket || isSubmitting}
                                  onClick={() =>
                                    submit("update", [monthArg], k, r.content_type)
                                  }
                                >
                                  {isSubmitting ? "Submitting…" : "Update"}
                                </Button>
                              </span>
                            </Tooltip>
                            <Tooltip title="Full re-index from scratch (skip_existing=false). Heavier — use after a schema bump.">
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Build />}
                                  disabled={!archiveBucket || isSubmitting}
                                  onClick={() =>
                                    submit("rebuild", [monthArg], k, r.content_type)
                                  }
                                >
                                  Rebuild
                                </Button>
                              </span>
                            </Tooltip>
                          </Stack>
                        ) : (
                          <Tooltip title="First build for a missing month: index this month's zips, then roll them up.">
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<CloudDownload />}
                                disabled={!archiveBucket || isSubmitting}
                                onClick={() =>
                                  submit("onboard", [monthArg], k, r.content_type)
                                }
                              >
                                {isSubmitting ? "Submitting…" : "Onboard"}
                              </Button>
                            </span>
                          </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        ))}
      </Stack>

      {notesTarget && (
        <EditNotesDialog
          open
          year={notesTarget.year}
          month={notesTarget.month}
          authorEmail={me?.email ?? null}
          onClose={() => setNotesTarget(null)}
        />
      )}
    </Container>
  );
}
